import { screenSizes, mq as sharedMq } from '@hrworks/design-system/utilities';

/**
 * Lokaler Ersatz fuer `mq` aus dem Design-System.
 *
 * Das Design-System formuliert seine Breakpoints in der Range-Syntax der Media Queries Level 4
 * (`@media (1024px > width >= 0px)`) und nutzt Boolean-Logik (`(not (pointer: coarse))`).
 * Beides beherrscht Safari erst ab 16.4. Aeltere iOS-Versionen verwerfen die komplette Regel als
 * ungueltig – dadurch fallen saemtliche Mobile-Styles aus und die Seite rendert in der
 * Desktop-Variante (beobachtet auf dem iPhone 8).
 *
 * Hier stehen deshalb dieselben Breakpoints in klassischer min-/max-width-Syntax. Nicht
 * betroffene Eintraege (`>=*`, `conditionalTransition`, `print`, …) werden unveraendert aus dem
 * Design-System uebernommen, damit spaetere Ergaenzungen dort automatisch mitkommen.
 *
 * Vorerst bewusst nur im Stellenportal. Die eigentliche Behebung gehoert ins Design-System.
 */

// Haelt die Bereiche bei gebrochenen Viewport-Breiten (Zoom, Geraete-Pixelverhaeltnis) lueckenlos.
const EPSILON = 0.02;

const toPx = (size: string) => Number.parseFloat(size);
const below = (size: string) => `(max-width: ${toPx(size) - EPSILON}px)`;
const from = (size: string) => `(min-width: ${toPx(size)}px)`;
const between = (lower: string, upper: string) => `${from(lower)} and ${below(upper)}`;

const screenSizeMediaQueries = {
  '<=xs': `@media ${below(screenSizes.sm)}`,
  '<=sm': `@media ${below(screenSizes.md)}`,
  '<=md': `@media ${below(screenSizes.lg)}`,
  '<=lg': `@media ${below(screenSizes.xl)}`,

  xs: `@media ${below(screenSizes.sm)}`,
  sm: `@media ${between(screenSizes.sm, screenSizes.md)}`,
  md: `@media ${between(screenSizes.md, screenSizes.lg)}`,
  lg: `@media ${between(screenSizes.lg, screenSizes.xl)}`,
} as const;

// `(pointer: fine)` statt `(not (pointer: coarse))`. Unterschied nur bei `pointer: none`
// (Geraete ganz ohne Zeigegeraet) – dort greifen Hover-Styles dann nicht mehr, was gewollt ist.
const pointerCondition = '(pointer: fine)';
const touchCondition = '(pointer: coarse)';
const smallDeviceCondition = below(screenSizes.lg);
const largeDeviceCondition = from(screenSizes.lg);

export const mq = {
  ...sharedMq,
  ...screenSizeMediaQueries,
  isSmallDevice: screenSizeMediaQueries['<=md'],
  isPointerDevice: `@media ${pointerCondition}`,
  isTouchOrSmallDevice: `@media ${touchCondition}, ${smallDeviceCondition}`,
  isPointerAndLargeDevice: `@media ${pointerCondition} and ${largeDeviceCondition}`,
  supportsHover: `@media (hover: hover) and ${pointerCondition}`,
} as const;
